import { useEffect, useRef, useState, useCallback } from "react";
import { createVideoSeekScheduler } from "./videoSeekScheduler";

/**
 * useScrubEngine — drives a 0..1 progress value from pointer gestures.
 *
 * Two backends:
 *  - mode='video': drives an HTMLVideoElement's currentTime
 *  - mode='canvas': draws pre-decoded ImageBitmap frames onto a <canvas>
 *
 * Both expose the same gesture handlers + progress state. Callers bind either
 * `videoRef` or `canvasRef` (with `frames` already extracted).
 *
 * Gesture rules (defaults from the PRD §15.1):
 *  - Slow drag updates progress immediately
 *  - Reverse drag rewinds progress
 *  - Hold finger still → progress paused
 *  - Quick swipe (velocity >= quickSwipeVelocity) → auto-complete
 *  - Release with progress >= thresholdRatio → complete
 *  - Release with progress < thresholdRatio → return to origin
 */
export function useScrubEngine({
  axis = "x",
  direction = 1,
  thresholdRatio = 0.5,
  quickSwipeVelocity = 0.45,
  dragStartThreshold = 6,
  durationFallback = 4000,
  mode = "video",        // 'video' | 'canvas'
  frames = null,         // ImageBitmap[] for canvas mode
  reverse = false,       // if true, displayed media plays end→start (progress=1 shows the first frame)
  onComplete,
  onCancel,
  enabled = true,
}) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const schedulerRef = useRef(null);
  const mountedRef = useRef(false);
  const modeRef = useRef(mode);
  modeRef.current = mode;
  const animationRef = useRef({ frame: null, generation: 0 });
  const cancelAnimation = useCallback(() => {
    const animation = animationRef.current;
    animation.generation += 1;
    if (animation.frame !== null) cancelAnimationFrame(animation.frame);
    animation.frame = null;
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    const scheduler = mode === "video" ? createVideoSeekScheduler() : null;
    schedulerRef.current = scheduler;
    return () => {
      mountedRef.current = false;
      cancelAnimation();
      scheduler?.dispose();
      if (schedulerRef.current === scheduler) schedulerRef.current = null;
    };
  }, [mode, cancelAnimation]);

  // Stable across progress renders, so awaiting completion does not restart
  // the caller's animation effect. Disposal resolves outstanding waits.
  const awaitSettled = useCallback((timeoutMs = 400) => {
    if (!mountedRef.current) return Promise.resolve(false);
    if (modeRef.current === "canvas") return Promise.resolve(true);
    return schedulerRef.current?.awaitSettled(timeoutMs) ?? Promise.resolve(false);
  }, []);
  const stateRef = useRef({
    active: false, scrubStarted: false,
    startX: 0, startY: 0, lastX: 0, lastY: 0,
    startTime: 0, lastTime: 0, surfaceSize: 1,
    progress: 0, velocity: 0,
  });
  const [progress, setProgress] = useState(0);
  const [scrubbing, setScrubbing] = useState(false);

  // Initial frame for canvas mode
  useEffect(() => {
    if (mode !== "canvas" || !canvasRef.current || !frames || !frames.length) return;
    drawFrame(canvasRef.current, frames[0]);
  }, [mode, frames]);

  const applyProgress = useCallback((p) => {
    const clamped = Math.max(0, Math.min(1, p));
    stateRef.current.progress = clamped;
    setProgress(clamped);
    // For reverse transitions the displayed media plays end→start, so we
    // map progress (always 0→1 user-facing) to (1−progress) on the media.
    const mediaP = reverse ? 1 - clamped : clamped;
    if (mode === "video") {
      const v = videoRef.current;
      if (v && v.duration && isFinite(v.duration)) {
        schedulerRef.current?.request(v, mediaP * v.duration);
      }
    } else if (mode === "canvas") {
      const canvas = canvasRef.current;
      if (canvas && frames && frames.length) {
        const idx = Math.min(frames.length - 1, Math.floor(mediaP * (frames.length - 1)));
        drawFrame(canvas, frames[idx]);
      }
    }
  }, [mode, frames, reverse]);

  const animateTo = useCallback((target, durationMs, done) => {
    cancelAnimation();
    const generation = animationRef.current.generation;
    const isCurrent = () => mountedRef.current && animationRef.current.generation === generation;
    const start = stateRef.current.progress;
    const t0 = performance.now();
    const tick = (t) => {
      if (!isCurrent()) return;
      animationRef.current.frame = null;
      const k = Math.min(1, (t - t0) / Math.max(60, durationMs));
      const eased = k < 0.5 ? 2 * k * k : 1 - Math.pow(-2 * k + 2, 2) / 2;
      const p = start + (target - start) * eased;
      applyProgress(p);
      if (k < 1) animationRef.current.frame = requestAnimationFrame(tick);
      else if (mode === "canvas") done?.();
      else awaitSettled().then(() => { if (isCurrent()) done?.(); });
    };
    animationRef.current.frame = requestAnimationFrame(tick);
  }, [applyProgress, awaitSettled, cancelAnimation, mode]);

  const onPointerDown = (e) => {
    if (!enabled) return;
    if (e.pointerType === "mouse" && e.button !== 0) return;
    cancelAnimation();
    const surface = e.currentTarget;
    const rect = surface.getBoundingClientRect();
    stateRef.current = {
      ...stateRef.current,
      active: true, scrubStarted: false,
      startX: e.clientX, startY: e.clientY,
      lastX: e.clientX, lastY: e.clientY,
      startTime: performance.now(), lastTime: performance.now(),
      surfaceSize: axis === "x" ? rect.width : rect.height,
      progress: stateRef.current.progress,
      velocity: 0,
    };
    try { surface.setPointerCapture?.(e.pointerId); } catch (_) { /* ignore */ }
  };

  const onPointerMove = (e) => {
    const s = stateRef.current;
    if (!s.active) return;
    const dx = e.clientX - s.startX;
    const dy = e.clientY - s.startY;
    const distAxis = axis === "x" ? dx : dy;
    const distOther = axis === "x" ? dy : dx;
    if (!s.scrubStarted) {
      const dist = Math.abs(distAxis);
      const dominant = Math.abs(distAxis) > Math.abs(distOther) * 1.3;
      if (dist >= dragStartThreshold && dominant) {
        s.scrubStarted = true;
        setScrubbing(true);
      } else {
        return;
      }
    }
    const now = performance.now();
    const dt = Math.max(1, now - s.lastTime);
    const moved = (axis === "x" ? e.clientX - s.lastX : e.clientY - s.lastY) * direction;
    s.velocity = moved / dt;
    s.lastX = e.clientX; s.lastY = e.clientY; s.lastTime = now;

    const p = (distAxis * direction) / s.surfaceSize;
    applyProgress(p);
  };

  const finish = (cancelled = false) => {
    const s = stateRef.current;
    s.active = false;
    setScrubbing(false);
    if (!s.scrubStarted) return;
    const p = s.progress;
    const dur = (mode === "video" && videoRef.current?.duration) ? videoRef.current.duration * 1000 : durationFallback;
    const speed = s.velocity;
    const goForward = !cancelled && (p >= thresholdRatio || speed >= quickSwipeVelocity);
    if (goForward) {
      animateTo(1, Math.max(180, dur * (1 - p) / 4), () => onComplete?.());
    } else {
      animateTo(0, Math.max(180, dur * p / 4), () => onCancel?.());
    }
  };

  return {
    videoRef, canvasRef,
    progress, scrubbing,
    setProgress: applyProgress, awaitSettled,
    surfaceProps: {
      onPointerDown,
      onPointerMove,
      onPointerUp: () => finish(false),
      onPointerCancel: () => finish(true),
      style: { touchAction: axis === "x" ? "pan-y" : "pan-x" },
    },
  };
}

function drawFrame(canvas, bitmap) {
  if (!bitmap) return;
  const ctx = canvas.getContext("2d");
  const cw = canvas.clientWidth, ch = canvas.clientHeight;
  if (canvas.width !== cw || canvas.height !== ch) {
    canvas.width = cw; canvas.height = ch;
  }
  // Letterbox the frame (contain) so the viewer sees the full detail —
  // `Math.max` (cover) used to crop top/bottom or sides on tall iPad portrait
  // and short landscape viewports.
  const scale = Math.min(cw / bitmap.width, ch / bitmap.height);
  const dw = bitmap.width * scale, dh = bitmap.height * scale;
  const dx = (cw - dw) / 2, dy = (ch - dh) / 2;
  ctx.clearRect(0, 0, cw, ch);
  ctx.drawImage(bitmap, dx, dy, dw, dh);
}

/**
 * Load already-extracted frames from a list of image URLs and convert them
 * to ImageBitmaps for use with the canvas-mode scrub engine. Use this when
 * the server has pre-baked a transition (transition.image_sequence_urls is
 * non-empty) — it skips the multi-second client-side video decode.
 *
 * Returns { ok, frames, reason } shaped identically to decodeVideoFrames.
 */
export async function decodeFramesFromUrls(urls, { signal, concurrency = 4 } = {}) {
  if (!urls || !urls.length) return { ok: false, frames: [], reason: "no-urls" };
  const controller = new AbortController();
  const abort = () => controller.abort();
  signal?.addEventListener("abort", abort, { once: true });
  if (signal?.aborted) controller.abort();
  const frames = new Array(urls.length);
  let next = 0;
  let failure = null;
  const worker = async () => {
    try {
      while (next < urls.length && !controller.signal.aborted) {
        const index = next++;
        const res = await fetch(urls[index], { credentials: "include", signal: controller.signal });
        if (!res.ok) throw new Error(`frame fetch ${res.status}`);
        const blob = await res.blob();
        if (controller.signal.aborted) break;
        const bitmap = await createImageBitmap(blob);
        if (controller.signal.aborted) { bitmap.close?.(); break; }
        frames[index] = bitmap;
      }
    } catch (error) {
      failure = failure || error.message;
      controller.abort();
    }
  };
  try {
    // Bounded even for a large cached sequence; abort the outstanding reads
    // on navigation/failure and reclaim every partially decoded bitmap.
    await Promise.all(Array.from({ length: Math.min(urls.length, Math.max(1, Math.min(4, concurrency))) }, worker));
    if (failure || controller.signal.aborted) {
      frames.forEach(frame => frame?.close?.());
      return { ok: false, frames: [], reason: failure || "aborted" };
    }
    return { ok: true, frames };
  } finally {
    signal?.removeEventListener("abort", abort);
  }
}

/**
 * Pre-decode a video into ImageBitmap frames at `fps` (capped at maxFrames).
 * Returns { frames, ok }. On failure returns ok=false.
 */
export async function decodeVideoFrames(videoUrl, { fps = 12, maxFrames = 48 } = {}) {
  try {
    const v = document.createElement("video");
    v.crossOrigin = "anonymous";
    v.muted = true;
    v.playsInline = true;
    v.preload = "auto";
    v.src = videoUrl;
    await new Promise((res, rej) => {
      v.onloadedmetadata = res;
      v.onerror = () => rej(new Error("video load failed"));
      setTimeout(() => rej(new Error("video load timeout")), 15000);
    });
    const dur = v.duration;
    if (!isFinite(dur) || dur <= 0) throw new Error("invalid duration");
    const count = Math.min(maxFrames, Math.max(4, Math.floor(dur * fps)));
    const w = v.videoWidth || 1280;
    const h = v.videoHeight || 720;
    const off = document.createElement("canvas");
    off.width = w; off.height = h;
    const ctx = off.getContext("2d");
    const frames = [];
    for (let i = 0; i < count; i++) {
      const t = (i / (count - 1)) * dur * 0.999;
      await new Promise((res, rej) => {
        const onSeeked = () => { v.removeEventListener("seeked", onSeeked); res(); };
        v.addEventListener("seeked", onSeeked);
        try { v.currentTime = t; } catch (e) { rej(e); }
        setTimeout(() => { v.removeEventListener("seeked", onSeeked); res(); }, 1500);
      });
      ctx.drawImage(v, 0, 0, w, h);
      try {
        const bmp = await createImageBitmap(off);
        frames.push(bmp);
      } catch (_) {
        // Cannot create bitmap (cross-origin tainted canvas) — bail out
        return { ok: false, frames: [], reason: "bitmap-failed" };
      }
    }
    return { ok: true, frames };
  } catch (e) {
    return { ok: false, frames: [], reason: e.message };
  }
}
